import Chip, { chipClasses } from "@mui/material/Chip";
import type { ChipProps } from "@mui/material/Chip";
import type { SxProps, Theme } from "@mui/material/styles";
import type { FC, ReactElement } from "react";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import { XIcon } from "../shared/icons";
import type { InlineInputState } from "../dynamicForm/inlineForm/inputs/InlineInputState";
import { multilineEllipsis } from "../shared/multilineEllipsis";
import type { IconProps } from "../shared/icons";
import { HACK_alignIconWithLabel, WhiteSpace } from "@privacy/ui";

type DynamicChipProps = {
  label: string;
  secondaryLabel?: string;
  onDelete?: ChipProps["onDelete"];
  disabled?: boolean;
  tabIndex?: number;
  renderIcon?: (props: IconProps) => ReactElement<IconProps> | undefined;
  inputState?: InlineInputState;
  variant: "entity" | "choice";
};

export const DynamicChip: FC<DynamicChipProps> = ({
  label,
  secondaryLabel,
  onDelete,
  disabled,
  tabIndex,
  renderIcon,
  inputState = "not-editing",
  variant,
}) => {
  const variantStyles = variants[variant];

  const icon = renderIcon?.({ size: 16, color: theme => theme.palette.text.primary }) ?? undefined;
  return (
    <Chip
      icon={icon ? <Stack sx={{ justifyContent: "center" }}> {icon} </Stack> : undefined}
      sx={{
        height: "auto",
        mixBlendMode: "multiply",
        [`& .${chipClasses.icon}`]: { color: "inherit", ml: inputState === "editing" ? 1 : 0 },
        [`& .${chipClasses.label}`]: {
          ...multilineEllipsis(1),
          ...HACK_alignIconWithLabel,
          width: "fit-content",
          lineHeight: "20px",
          maxWidth: secondaryLabel ? "420px" : "280px",
          py: "2px",
          px: icon ? 2 : 1.5,
          pr: inputState === "editing" ? 2 : undefined,
        },
        [`& .${chipClasses.deleteIcon}`]: {
          "color": "inherit",
          "position": "relative",
          "zIndex": 1,
          "display": "inline-flex",
          "&::before": { content: '""', position: "absolute", inset: "-7px" },
        },
        ...variantStyles[inputState],
      }}
      disabled={disabled}
      tabIndex={tabIndex}
      label={
        <>
          {label}
          {secondaryLabel && (
            <>
              <WhiteSpace />
              <Box
                component="span"
                sx={{
                  ml: 0.5,
                  fontSize: theme => theme.typography.pxToRem(12),
                  color: theme => theme.palette.text.tertiary,
                }}
              >
                {secondaryLabel}
              </Box>
            </>
          )}
        </>
      }
      onDelete={inputState === "editing" ? onDelete : undefined}
      deleteIcon={
        <Box component="span">
          <XIcon size={10} color={theme => theme.palette.text.secondary} />
        </Box>
      }
    />
  );
};

const choiceStyles: SxProps<Theme> = {
  borderRadius: "4px",
  bgcolor: theme => `${theme.palette.grey["300"]} !important`,
  color: theme => `${theme.palette.text.primary} !important`,
  fontWeight: "600",
  fontSize: theme => theme.typography.pxToRem(12),
};

const baseEntityStyles: SxProps<Theme> = {
  color: theme => theme.palette.text.primary,
  fontSize: theme => theme.typography.pxToRem(14),
};
const notEditingEntityStyles: SxProps<Theme> = { ...baseEntityStyles, py: 0, bgcolor: "transparent" };

type InputStateToStyles = Record<InlineInputState, ChipProps["sx"]>;
const variants: Record<DynamicChipProps["variant"], InputStateToStyles> = {
  entity: {
    "editing": {
      ...baseEntityStyles,
      borderRadius: "4px",
      boxShadow: theme => `0 0 0 1px ${theme.palette.grey["300"]}`,
      bgcolor: theme => theme.palette.grey["100"],
    },
    "not-editing": notEditingEntityStyles,
    "readonly": notEditingEntityStyles,
  },
  choice: {
    "editing": choiceStyles,
    "not-editing": choiceStyles,
    "readonly": choiceStyles,
  },
};
